import { createApi } from '@reduxjs/toolkit/query/react';
import { apiAuthenticatedQuery } from './baseQuery';
import {
  CarCategory,
  CarDetail,
  CarQualification,
  CarsRequestData,
  GetCarDetailRequest,
  getCarsRequest,
  getCarsResponse,
} from '../types/car';
import { extinctFalsyFromParams } from './nullExtict';
import { errorTagTypes, handleErrorTags } from './handleErrorTags';
import { Middleware } from '@reduxjs/toolkit';
import { carAllocationsApi } from './carAllocationApi';

export const carApi = createApi({
  baseQuery: apiAuthenticatedQuery,
  reducerPath: 'car',
  tagTypes: ['Car', ...errorTagTypes],
  endpoints: (build) => ({
    getCars: build.query<getCarsResponse, getCarsRequest>({
      query: (param) => ({
        url: `/cars`,
        method: 'GET',
        params: extinctFalsyFromParams(param),
      }),
      providesTags: (res, error) => [
        ...(res?.cars || []).map((c) => ({
          type: 'Car' as const,
          id: c.carId,
        })),
        { type: 'Car', id: 'List' },
        ...handleErrorTags(error),
      ],
    }),
    postCars: build.mutation<void, CarsRequestData>({
      query: (body) => ({
        url: `/cars`,
        method: 'POST',
        body: extinctFalsyFromParams(body),
      }),
      invalidatesTags: [{ type: 'Car', id: 'List' }],
    }),
    getCarDetail: build.query<CarDetail, GetCarDetailRequest>({
      query: (param) => ({
        url: `/cars/${param.carId}`,
      }),
      providesTags: (res, error) => [
        {
          type: 'Car' as const,
          id: res?.carId,
        },
        { type: 'Car', id: 'List' },
        ...handleErrorTags(error),
      ],
    }),
    putCars: build.mutation<void, { carId: number; data: CarsRequestData }>({
      query: ({ carId, data }) => ({
        url: `/cars/${carId}`,
        method: 'PUT',
        body: extinctFalsyFromParams(data, [
          'unavailableStartDate',
          'unavailableEndDate',
          'qualificationIds',
        ]),
      }),
      invalidatesTags: (_res, _err, args) => [
        { type: 'Car', id: args.carId },
        { type: 'Car', id: 'List' },
      ],
    }),
    deleteCars: build.mutation<void, number>({
      query: (carId) => ({
        url: `/cars/${carId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Car', id },
        { type: 'Car', id: 'List' },
      ],
    }),
    getCarCategories: build.query<CarCategory[], void>({
      query: () => ({
        url: `/car/categories`,
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    getCarQualifications: build.query<CarQualification[], void>({
      query: () => ({
        url: `/car/qualifications`,
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    postRegisterCsvByCar: build.mutation<void, File>({
      query: (file) => {
        const body = new FormData();
        body.append('csv', file);
        body.append('type', 'car');
        return {
          url: `/register-csv`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: [
        {
          type: 'Car',
          id: 'List',
        },
      ],
    }),
  }),
});

/**
 * 車両編集、新規追加後に配車一覧取得 API のキャッシュが破棄されるよう保証するための Middleware。
 *
 * `carApi` のエンドポイントに記載した `invalidatesTags` は
 * `carApi` 内のエンドポイントしかキャッシュを破棄しないため、
 * 別の API 定義内のエンドポイントのキャッシュ無効化には別途外部からキャッシュタグを無効化する必要がある。
 *
 * API アクセスすれば確実に呼ばれるようにするため Middleware として記述する。
 */
export const afterUpdateCarMiddleware: Middleware =
  (store) => (next) => (action) => {
    if (
      carApi.endpoints.putCars.matchFulfilled(action) ||
      carApi.endpoints.postCars.matchFulfilled(action)
    ) {
      store.dispatch(
        carAllocationsApi.util.invalidateTags([
          { type: 'CarAllocations', id: 'Car' },
          { type: 'CarAllocations', id: 'Driver' },
        ])
      );
    }
    return next(action);
  };

export const {
  useGetCarsQuery,
  usePostCarsMutation,
  useGetCarDetailQuery,
  usePutCarsMutation,
  useDeleteCarsMutation,
  useGetCarCategoriesQuery,
  useGetCarQualificationsQuery,
  usePostRegisterCsvByCarMutation,
} = carApi;
